import {
  QSelectItem,
  QSpacer,
  QSpinner,
  QStack,
  QText,
  QLink,
  useCurrentUser as useQCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useUmAnalytics } from '../../../analytics';
import {
  getCompanyFromMedtech,
  getGroups,
  getUserAssociations,
  getUsersByCompanyId,
  inviteUserInMedtech,
} from '../../../api/medtech-api';
import {
  MinimumRequiredAdminsErrorMessage,
  MinimumRequiredQualityUsersErrorMessage,
} from '../../../errors/__displayStrings__';
import { ErrorCodes, getErrorMessageOrDefault } from '../../../errors/um-errors';
import { Group, InviteUserRequest, User, UserTableData } from '../../../types';
import redirectToLogin from '../../../utils/redirect';
import { createErrorToast, createSuccessToast } from '../../../utils/toast.utils';
import { UserActionItems } from '../../../utils/umfe.enums';
import {
  isBillingUser,
  isInviteAccepted,
  isUserChangingSelf,
  isUserQm,
  sortGroupsByName,
  sortUsersByFullName,
} from '../../../utils/users.utils';
import { isAxiosError } from '../../../utils/validation/axios';
import { CanAccessBilling } from '../../../utils/validation/medtechPermissions';
import {
  AddToGroupModal,
  InviteUserFormModal,
  ManageGroupsModal,
  RemoveUserGroupsModal,
  RemoveUserModal,
  SetRoleModal,
  UserModal,
} from '../../modals';
import { useFlagProvider } from '../../../utils/flagProvider';
import { UserActionButtons } from './UserActionButtons';
import { UserList } from './UserList';
import { UserRemovalService } from '../../../services/delete/deleteUser';
import { getUserControls } from '../../../api/compliance-api';
import { useComplianceAccess } from '../../../hooks/useComplianceAccess';

type CompanyUserCount = {
  activeUsersCount: number;
  activeUsersLimit: number;
  totalUsersCount: number;
  totalUsersLimit: number;
  unlimitedBasicUsers: boolean;
};

const Users = () => {
  const { t } = useTranslation();
  const { userId: currentUserId, companyId: currentCompanyId, permissions } = useQCurrentUser();
  const canAccessBilling = Boolean(permissions?.[CanAccessBilling]);
  const hasComplianceAccess = useComplianceAccess();
  const analytics = useUmAnalytics();
  const { showToast } = useToastProvider();
  const navigate = useNavigate();
  // Data from API
  const [rawUsers, setRawUsers] = useState<User[]>();
  const [userCounts, setUserCounts] = useState<CompanyUserCount>();
  const [groups, setGroups] = useState<Group[]>();

  // State selection
  const [clickedUser, setClickedUser] = useState<User>();
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [selectedGroups, setSelectedGroups] = useState<QSelectItem[]>([]);
  const areUsersSelected = selectedUserIds.size > 0;

  // Loading flags
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [isFetchUserAssociations, setFetchUserAssociations] = useState(false);
  // Derive filtered/sorted users from rawUsers — computed in the same render pass,
  // eliminating the extra render cycle that the previous useEffect approach caused.
  const users = useMemo(() => {
    if (!rawUsers) {
      return undefined;
    }
    return rawUsers
      .filter((row) =>
        selectedGroups.length === 0
          ? true
          : !!row?.groups?.find((group) =>
              selectedGroups.some((sG) => sG.label.toUpperCase() === group?.name?.toUpperCase()),
            ),
      )
      .sort(sortUsersByFullName);
  }, [selectedGroups, rawUsers]);

  const isLoading = !(rawUsers && users && userCounts && groups);

  const { isFlagEnabled } = useFlagProvider();
  const isSeatLimitSelfServeEnabled = isFlagEnabled('removeUserSeatLimit');
  const showProfileFields = isFlagEnabled('userAttributes');

  // Modal controls
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [isEditUserModalOpen, setEditUserModalOpen] = useState(false);
  const [isSetRoleModalOpen, setRoleModalOpen] = useState(false);
  const [isAddGroupModalOpen, setAddGroupModalOpen] = useState(false);
  const [isRemoveUserGroupsModalOpen, setRemoveUserGroupsModalOpen] = useState(false);
  const [isManageGroupsModalOpen, setManageGroupsModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [isRemoveUserWithEntities, setRemoveUserWithEntities] = useState<boolean>(false);

  const closeRoleModal = useCallback(() => setRoleModalOpen(false), [setRoleModalOpen]);
  const closeAddGroupModal = useCallback(() => setAddGroupModalOpen(false), [setAddGroupModalOpen]);
  const closeRemoveUserGroupsModal = useCallback(
    () => setRemoveUserGroupsModalOpen(false),
    [setRemoveUserGroupsModalOpen],
  );
  const closeManageGroupsModal = useCallback(() => setManageGroupsModalOpen(false), [setManageGroupsModalOpen]);
  const closeInviteModal = useCallback(() => setInviteModalOpen(false), [setInviteModalOpen]);
  const closeEditUserModal = useCallback(() => setEditUserModalOpen(false), [setEditUserModalOpen]);
  const closeDeleteUserModal = useCallback(() => setDeleteModalOpen(false), [setDeleteModalOpen]);

  const getCompanyUserCount = async (companyId: number) => {
    const company = await getCompanyFromMedtech(companyId);
    const {
      activeUsersCount = 0,
      activeUsersLimit,
      totalUsersCount = 0,
      totalUsersLimit,
      unlimitedBasicUsers,
    } = company;
    setUserCounts({
      activeUsersCount,
      activeUsersLimit,
      totalUsersCount,
      totalUsersLimit,
      unlimitedBasicUsers: unlimitedBasicUsers ?? false,
    });
  };

  const getRawUsers = async (companyId: number) => {
    const { data } = await getUsersByCompanyId(companyId);

    setRawUsers(data);
  };

  const getUsers = useCallback(async (companyId: number) => {
    await Promise.all([getRawUsers(companyId), getCompanyUserCount(companyId)]);
  }, []);

  const fetchGroups = useCallback(
    async (companyId: number) => {
      const { data } = await getGroups(companyId);
      setGroups(data.sort(sortGroupsByName));
    },
    [getGroups, setGroups, sortGroupsByName],
  );

  // Get users
  useEffect(() => {
    getUsers(currentCompanyId);
  }, [currentCompanyId]);

  // Get groups for drop-down menu
  useEffect(() => {
    fetchGroups(currentCompanyId);
  }, [currentCompanyId]);

  useEffect(() => {
    if (isRemoveUserWithEntities && clickedUser) {
      navigate(`/admin/users/${clickedUser.id}/remove`);
    }
  }, [isRemoveUserWithEntities]);

  const getAssociatedControls = useCallback(
    async (userId: number, companyId: number) => {
      if (hasComplianceAccess) {
        return getUserControls(userId, companyId);
      }
      return Promise.resolve({ data: { total: 0 } });
    },
    [hasComplianceAccess],
  );

  useEffect(() => {
    if (clickedUser && isFetchUserAssociations) {
      Promise.all([
        getUserAssociations(clickedUser.id, currentCompanyId),
        getAssociatedControls(clickedUser.id, currentCompanyId),
      ])
        .then(([res, controlsResponse]) => {
          const { approver, issues, owner, reviewer, tasks, default_owner: defaultOwner } = res.data;
          const { total: controlsLength } = controlsResponse.data;
          if (
            (approver.documents.length ||
              approver.periodic_reviews.length ||
              owner.documents.length ||
              owner.change_requests.length ||
              owner.event_templates.length ||
              owner.document_templates.length ||
              issues.issues.length ||
              reviewer.documents.length ||
              tasks.tasks.length ||
              defaultOwner.event_templates.length) > 0 ||
            controlsLength > 0
          ) {
            setRemoveUserWithEntities(true);
          } else {
            setDeleteModalOpen(true);
            setFetchUserAssociations(false);
          }
        })
        .catch(() => {
          showToast(createErrorToast(t('messages.error.extractOwnedItems'), 'users-extract-owned-items-error-toast'));
        });
    }
  }, [isFetchUserAssociations, getAssociatedControls]);

  const getUserById = (id: number) => {
    return users?.find((user) => user.id === id);
  };

  const currentlySelectedUsers = useMemo(() => {
    return rawUsers ? rawUsers.filter((user) => selectedUserIds.has(user.id)) : [];
  }, [selectedUserIds, rawUsers]);

  const sendUserInvitation = async (companyId: number, user: User) => {
    const { email, role, isAdmin } = user;
    const isBilling = isBillingUser(user);
    const payload: InviteUserRequest = {
      email,
      isAdmin,
      isBilling,
      role,
    };

    try {
      await inviteUserInMedtech(companyId, payload);
      await getUsers(companyId);
      showToast(createSuccessToast(t('messages.success.invitationResent'), 'users-resend-invite-success-toast'));
      analytics.inviteUserSuccess(isAdmin, { role });
    } catch (err: unknown) {
      const status = isAxiosError(err) ? err?.response?.status : undefined;
      showToast(createErrorToast(t('messages.error.resendInvitation'), 'users-resend-invite-error-toast'));
      analytics.inviteUserFailed(isAdmin, { role, status });
    }
  };

  const handleCancelUser = async (companyId: number, user: User) => {
    setDeleting(true);
    const { id, email } = user;
    const cancelInvitePayload = { email };

    try {
      await UserRemovalService.cancelUserInvite(companyId, id, cancelInvitePayload);
      await getUsers(companyId);
      setDeleteModalOpen(false);
      setSelectedUserIds(new Set());
      showToast(createSuccessToast(t('messages.success.invitationCanceled'), 'users-cancel-invite-success-toast'));
      analytics.cancelUserSuccess();
    } catch (err: unknown) {
      const status = isAxiosError(err) ? err?.response?.status : undefined;
      const errorMessageMap = {
        [ErrorCodes.MINIMUM_ADMINS_REQUIRED]: MinimumRequiredAdminsErrorMessage,
      };

      const errorMessage = getErrorMessageOrDefault(err, errorMessageMap, t('messages.error.cancelInvitation'));

      showToast(createErrorToast(errorMessage, 'users-cancel-invite-error-toast'));
      analytics.cancelUserFailed({ status, errorMessage });
    } finally {
      setDeleting(false);
    }
  };

  const cancelUserCallback = useCallback(
    () => handleCancelUser(currentCompanyId, clickedUser!),
    [currentCompanyId, clickedUser],
  );

  const handleDeleteUser = async (companyId: number, user: User) => {
    try {
      setDeleting(true);

      const { id } = user;
      const isQm = isUserQm(user);

      await UserRemovalService.deleteUser(companyId, id, { id, isQm });

      if (isUserChangingSelf(currentUserId, user.id)) {
        redirectToLogin();
      }

      await getUsers(companyId);
      setDeleteModalOpen(false);
      setSelectedUserIds(new Set());
      showToast(createSuccessToast(t('messages.success.userRemoved'), 'users-remove-success-toast'));
      analytics.removeUserSuccess();
    } catch (err: unknown) {
      const status = isAxiosError(err) ? err?.response?.status : undefined;

      const errorMessageMap = {
        [ErrorCodes.MINIMUM_ADMINS_REQUIRED]: MinimumRequiredAdminsErrorMessage,
        [ErrorCodes.MINIMUM_QM_USER_REQUIRED]: MinimumRequiredQualityUsersErrorMessage,
      };

      const errorMessage = getErrorMessageOrDefault(err, errorMessageMap, t('messages.error.removeUser'));

      showToast(createErrorToast(errorMessage, 'users-remove-error-toast'));
      analytics.removeUserFailed({ status, errorMessage });
    } finally {
      setDeleting(false);
    }
  };

  const deleteUserCallback = useCallback(
    () => handleDeleteUser(currentCompanyId, clickedUser!),
    [currentCompanyId, clickedUser],
  );

  const handleUserAction = useCallback(
    (actionItemId: string, userRow: UserTableData): void => {
      const user = getUserById(userRow.id);
      if (!user) {
        return;
      }
      setClickedUser(user);
      if (actionItemId === UserActionItems.EDIT_USER.id) {
        navigate(`/admin/users/${user.id}`);
        return;
      }
      if (actionItemId === UserActionItems.CANCEL_INVITE.id) {
        setDeleteModalOpen(true);
      }
      if (actionItemId === UserActionItems.REMOVE_USER.id) {
        setFetchUserAssociations(true);
      }
      if (actionItemId === UserActionItems.REMOVE_DECLINED_USER.id) {
        setDeleteModalOpen(true);
      }
      if (actionItemId === UserActionItems.RESEND_INVITE.id) {
        sendUserInvitation(currentCompanyId, user);
      }
    },
    [
      navigate,
      setClickedUser,
      setEditUserModalOpen,
      setFetchUserAssociations,
      setDeleteModalOpen,
      sendUserInvitation,
      getUserById,
    ],
  );

  const groupFilterAction = useCallback(
    (groupName?: string) => {
      if (!groupName) {
        setSelectedGroups([]);
        return;
      }

      const selectedGroupItem = groups?.find((group) => group.name.toLowerCase() === groupName.toLowerCase())!;

      const newSelectedGroups = selectedGroups.filter(
        (group) => group.label.toLowerCase() !== selectedGroupItem.name.toLowerCase(),
      );

      // This means the selected group was not previously in the selected groups array so we need to add it
      if (newSelectedGroups.length === selectedGroups.length) {
        newSelectedGroups.push({ value: selectedGroupItem.id.toString(), label: selectedGroupItem.name });
      }
      setSelectedGroups(newSelectedGroups);
    },
    [groups, selectedGroups],
  );

  const refetchUsers = useCallback(() => getUsers(currentCompanyId), [currentCompanyId]);

  return isLoading ? (
    <QSpinner size="sm" />
  ) : (
    <QStack direction="column">
      <QText aria-label="userCount-text" fontSize="sm">
        <>
          {t('users.editUsersUsage', {
            activeUsersCount: userCounts.activeUsersCount,
            activeUsersLimit: userCounts.activeUsersLimit,
          })}
          {' | '}
          {userCounts.unlimitedBasicUsers
            ? t('users.basicUsersUsageUnlimited', {
                basicUsersCount: userCounts.totalUsersCount - userCounts.activeUsersCount,
              })
            : t('users.basicUsersUsage', {
                basicUsersCount: userCounts.totalUsersCount - userCounts.activeUsersCount,
                basicUsersLimit: userCounts.totalUsersLimit - userCounts.activeUsersLimit,
              })}
        </>{' '}
        {canAccessBilling && (
          <QLink isCrossMFE data-cy="upgrade-subscription" href="/manage/billing">
            {isSeatLimitSelfServeEnabled ? t('users.addMoreSeats') : t('users.upgradeSubscription')}
          </QLink>
        )}
      </QText>
      <QSpacer p={1} />
      <UserActionButtons
        areUsersSelected={areUsersSelected}
        currentlySelectedUsers={currentlySelectedUsers}
        setRoleModalOpen={setRoleModalOpen}
        setAddGroupModalOpen={setAddGroupModalOpen}
        setRemoveUserGroupsModalOpen={setRemoveUserGroupsModalOpen}
        setInviteModalOpen={setInviteModalOpen}
      />
      <UserList
        users={users}
        selectedUserIds={selectedUserIds}
        groups={groups}
        selectedGroups={selectedGroups}
        setSelectedGroups={setSelectedGroups}
        setSelectedUserIds={setSelectedUserIds}
        handleUserAction={handleUserAction}
        filterByGroup={groupFilterAction}
        showProfileFields={showProfileFields}
      />
      {/* Modals */}
      {isSetRoleModalOpen && (
        <SetRoleModal
          closeModal={closeRoleModal}
          users={currentlySelectedUsers}
          companyId={currentCompanyId}
          getUsers={refetchUsers}
        />
      )}
      {isAddGroupModalOpen && (
        <AddToGroupModal
          closeModal={closeAddGroupModal}
          users={currentlySelectedUsers}
          groups={groups}
          getUsers={refetchUsers}
        />
      )}
      {isManageGroupsModalOpen && (
        <ManageGroupsModal
          closeModal={closeManageGroupsModal}
          users={currentlySelectedUsers}
          groups={groups!}
          getUsers={refetchUsers}
        />
      )}
      {isRemoveUserGroupsModalOpen && (
        <RemoveUserGroupsModal
          closeModal={closeRemoveUserGroupsModal}
          users={currentlySelectedUsers}
          getUsers={refetchUsers}
        />
      )}
      {isInviteModalOpen && (
        <InviteUserFormModal
          closeModal={closeInviteModal}
          companyId={currentCompanyId}
          getUsers={refetchUsers}
          unlimitedBasicUsers={userCounts.unlimitedBasicUsers}
        />
      )}
      {isEditUserModalOpen && clickedUser && (
        <UserModal
          user={clickedUser}
          closeModal={closeEditUserModal}
          companyId={currentCompanyId}
          getUsers={refetchUsers}
        />
      )}
      {/* Confirm Delete Modal */}
      {isDeleteModalOpen && clickedUser && (
        <RemoveUserModal
          closeModal={closeDeleteUserModal}
          removeUser={isInviteAccepted(clickedUser.invite_status) ? deleteUserCallback : cancelUserCallback}
          isDeleting={isDeleting}
          user={clickedUser}
        />
      )}
    </QStack>
  );
};

export default Users;
